"use client";

import { cn } from "../lib/utils";
import { useCartAssistant, useCartAssistantInput } from "../lib/cartAssistant/CartAssistantProvider";
import { SendIcon, PaperclipIcon, X, Loader2, ChevronDown, ChevronUp, Square } from "lucide-react";
import * as React from "react";
import { processImage } from "../../services/imageUtils";
import { SupabaseClient } from "@supabase/supabase-js";
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../lib/ui-store';
import { liveState } from '../lib/store';

/**
 * Message Input component
 *
 * This component provides a text input for the user to send messages to the AI.
 * It automatically integrates with the CartAssistant context to handle message submission.
 */

export interface MessageInputProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Placeholder text for the input */
  placeholder?: string;
  onPreviewImage: (url: string | null) => void;
  supabaseClient?: SupabaseClient | null;
  userId?: string;
}

/**
 * Component to handle image preview for StagedImage items
 */
function ImageThumbnail({ 
  file, 
  onPreview 
}: { 
  file: File, 
  onPreview: (url: string) => void 
}) {
  const [url, setUrl] = React.useState<string>("");

  React.useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!url) return <div className="w-full h-full bg-gray-50 animate-pulse" />;

  return (
    <img 
      src={url} 
      alt={file.name} 
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
      onClick={() => onPreview(url)}
    />
  );
}

export function MessageInput({
  className, 
  placeholder,
  onPreviewImage,
  supabaseClient,
  userId,
  ...restTextareaProps 
}: MessageInputProps) {
  const { t } = useTranslation();
  const effectivePlaceholder = placeholder || t('chat.ask_something');
  
  const { 
    value, 
    setValue, 
    submit, 
    isPending: inputIsPending, 
    isDisabled,
    images: stagedImages,
    addImage,
    removeImage,
    clearImages
  } = useCartAssistantInput();
  
  const { currentThreadId, cancelRun, isIdle } = useCartAssistant();
  const [isUploading, setIsUploading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(true);
  const { setNotification } = useUIStore();
  
  const isPending = inputIsPending || isUploading || isSubmitting;
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const wasLargeTextRef = React.useRef(false);

  // Logic for large text detection
  const isLargeText = value.length > 150 || value.split('\n').length > 4;
  const canMinimize = stagedImages.length > 0 && isLargeText;

  // Auto-expand/collapse logic
  React.useEffect(() => {
    // If it transitions from small to large text, and we have images, auto-minimize
    if (isLargeText && !wasLargeTextRef.current && stagedImages.length > 0) {
      setIsMinimized(true);
    }
    // If it transitions to small text, we must expand
    if (!isLargeText) {
      setIsMinimized(false);
    }
    wasLargeTextRef.current = isLargeText;
  }, [isLargeText, stagedImages.length]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // 1. Optimize the image before adding it to the chat
        // Use 1200px (same as the basket import flow) so Gemini can read receipt headers/dates
        const { blob } = await processImage(file, 1200);
        
        // 2. Sanitize filename: replace all special characters with underscores and force .jpg
        const baseName = file.name.split('.').slice(0, -1).join('.') || 'image';
        const sanitizedBase = baseName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
        const uniqueId = Date.now().toString(36) + Math.random().toString(36).substring(2, 5);
        const sanitizedName = `chat_${uniqueId}_${sanitizedBase}.jpg`;
        
        // 3. Create a clean File object
        const optimizedFile = new File([blob], sanitizedName, { type: 'image/jpeg' });
        
        // 4. Add to CartAssistant SDK's native staging area
        await addImage(optimizedFile);
        
      } catch (err) {
        console.error("Image processing failed:", err);
      }
      e.target.value = ''; 
    }
  };

  const handleSend = async () => {
    if (!value.trim() && stagedImages.length === 0) return;
    if (isPending || isDisabled) return;

    setIsSubmitting(true);
    let finalMessageText = value.trim();

    // 1. Image Sync (IndexedDB) - Save references and add markers to text for immediate UI visibility
    if (stagedImages.length > 0) {
      setIsUploading(true);
      try {
        const { ImageCacheService } = await import("../../services/imageCacheService");
        for (const img of stagedImages) {
          const localUrl = await ImageCacheService.saveChatImage(img.file);
          if (localUrl) {
            // OPTIMISTIC MARKER: Add to text so the user sees the image in their bubble immediately
            finalMessageText = finalMessageText ? `${finalMessageText}\n\n[img:${localUrl}]` : `[img:${localUrl}]`;
            await ImageCacheService.setThreadImageRef(currentThreadId, localUrl, img.file.name);
            liveState.lastThreadImageRef = { url: localUrl, filename: img.file.name, timestamp: Date.now(), threadId: currentThreadId };
          }
        }
      } catch (error) {
        console.error("Local image cache failed:", error);
      } finally {
        setIsUploading(false);
      }
    }

    try {
      // 2. Submit to CartAssistant, then clear the composer optimistically
      setValue(finalMessageText);
      const submitPromise = submit();
      setValue("");
      clearImages();
      setIsMinimized(false);
      await submitPromise;
    } catch (err) {
      console.error("[CartAssistant] Submission failed:", err);
      setValue(finalMessageText);
      setNotification({
        id: Date.now(),
        message: t('chat.thread_error_recovery'),
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-resize textarea
  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      if (isMinimized) {
        textarea.style.height = "40px";
      } else {
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
      }
    }
  }, [value, isMinimized]);

  return (
    <div className={cn("relative flex flex-col gap-1.5 px-3 pt-2 pb-3 bg-white", className)}>
      {stagedImages.length > 0 && (
        <div className="flex flex-col gap-1.5 mb-1 animate-in fade-in slide-in-from-top-1 duration-300">
          <div className="flex items-center justify-between px-1">
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">
              {t('chat.attached')} ({stagedImages.length})
            </span>
            {canMinimize && (
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                className="flex items-center gap-1 px-2 py-0.5 bg-gray-50 hover:bg-gray-100 rounded-full text-gray-400 transition-all active:scale-90 border border-gray-100"
              >
                {isMinimized ? <ChevronUp size={10} strokeWidth={3} /> : <ChevronDown size={10} strokeWidth={3} />}
                <span className="text-[8px] font-black uppercase tracking-tighter">{isMinimized ? t('chat.expand_all') : t('chat.minimize')}</span>
              </button>
            )}
          </div>
          
          {!isMinimized && (
            <div className="flex flex-wrap gap-2">
              {stagedImages.map((img) => (
                <div 
                  key={img.id}
                  className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-100 cursor-pointer group shadow-sm"
                >
                  <ImageThumbnail file={img.file} onPreview={onPreviewImage} />
                  <button
                    onClick={(e) => { e.stopPropagation(); removeImage(img.id); }} 
                    className="absolute top-1 right-1 bg-black/60 backdrop-blur-md rounded-full p-1 text-white hover:bg-rose-500 transition-colors opacity-0 group-hover:opacity-100"
                    title={t('common.delete')}
                  >
                    <X size={10} strokeWidth={3} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex items-end gap-1 rounded-[1.5rem] border border-gray-200 bg-gray-50 pl-1.5 pr-1.5 py-1.5 transition-all focus-within:border-emerald-300 focus-within:bg-white focus-within:ring-4 focus-within:ring-emerald-500/10">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept="image/*"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:pointer-events-none disabled:opacity-40"
          disabled={isPending || isDisabled}
          title={t('chat.attached')}
          aria-label={t('chat.attached')}
        >
          <PaperclipIcon className="h-4 w-4" />
        </button>
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={effectivePlaceholder}
          disabled={isDisabled}
          className="flex-1 min-h-[36px] max-h-[200px] resize-none bg-transparent border-0 px-1 py-2 text-[14px] leading-5 text-[#1a2b3c] placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60"
          {...restTextareaProps}
        />
        {!isIdle && !isPending ? (
          <button
            onClick={() => cancelRun()}
            className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center bg-[#1a2b3c] text-white hover:bg-rose-500 transition-colors"
            title={t('chat.stop_generation')}
            aria-label={t('chat.stop_generation')}
          >
            <Square className="h-3.5 w-3.5 fill-current" />
          </button>
        ) : (
          <button
            onClick={() => handleSend()}
            disabled={(!value.trim() && stagedImages.length === 0) || isPending || isDisabled}
            className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center bg-emerald-600 text-white shadow-sm shadow-emerald-600/30 hover:bg-emerald-700 transition-colors disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none disabled:pointer-events-none"
            aria-label={t('contact.send')}
          >
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendIcon className="h-4 w-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
